import {
  supabase,
  Preset,
  Schedule,
  ScheduleEntry,
  GapRule,
  DayPlan,
  DayPlanItem,
  PresetGroup,
  PresetGroupMember,
} from "./supabase";
import { generateDayPlan, reflowAfterChange, isNextDay } from "./scheduler";

export async function getBuiltInPresets(): Promise<Preset[]> {
  const { data, error } = await supabase
    .from("presets")
    .select("*")
    .is("owner_user_id", null)
    .order("name");

  if (error) throw error;
  return data || [];
}

export async function getUserPresets(): Promise<Preset[]> {
  const { data, error } = await supabase
    .from("presets")
    .select("*")
    .not("owner_user_id", "is", null)
    .order("name");

  if (error) throw error;
  return data || [];
}

export async function createPreset(
  preset: Omit<Preset, "id" | "created_at" | "updated_at">
): Promise<Preset> {
  const { data, error } = await supabase
    .from("presets")
    .insert(preset)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePreset(
  id: string,
  updates: Partial<Preset>
): Promise<Preset> {
  const { data, error } = await supabase
    .from("presets")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deletePreset(id: string): Promise<void> {
  const { error } = await supabase.from("presets").delete().eq("id", id);

  if (error) throw error;
}

export async function duplicatePreset(presetId: string): Promise<Preset> {
  const { data: original, error: fetchError } = await supabase
    .from("presets")
    .select("*")
    .eq("id", presetId)
    .single();

  if (fetchError) throw fetchError;

  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("presets")
    .insert({
      owner_user_id: user.user.id,
      name: original.name,
      tag: original.tag,
      food_relation: original.food_relation,
      priority: original.priority,
      description: original.description,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getActiveSchedule(): Promise<
  (Schedule & { entries: ScheduleEntry[]; gap_rules: GapRule[] }) | null
> {
  const { data: schedule, error: scheduleError } = await supabase
    .from("schedules")
    .select("*")
    .eq("is_active", true)
    .maybeSingle();

  if (scheduleError) throw scheduleError;
  if (!schedule) return null;

  const { data: entries, error: entriesError } = await supabase
    .from("schedule_entries")
    .select("*")
    .eq("schedule_id", schedule.id)
    .order("desired_offset_min");

  if (entriesError) throw entriesError;

  const enrichedEntries = await Promise.all(
    (entries || []).map(async (entry) => {
      if (entry.preset_id) {
        const { data: preset } = await supabase
          .from("presets")
          .select("*")
          .eq("id", entry.preset_id)
          .maybeSingle();
        return { ...entry, preset: preset || undefined };
      } else if (entry.group_id) {
        const { data: group } = await supabase
          .from("preset_groups")
          .select("*")
          .eq("id", entry.group_id)
          .maybeSingle();

        if (group) {
          const { data: members } = await supabase
            .from("preset_group_members")
            .select("preset:presets(*)")
            .eq("group_id", group.id);

          const presets = (members || [])
            .map((m) => m.preset as Preset)
            .filter(Boolean);

          return { ...entry, group: { ...group, presets } };
        }
      }
      return entry;
    })
  );

  const { data: gapRules, error: rulesError } = await supabase
    .from("gap_rules")
    .select("*")
    .eq("schedule_id", schedule.id);

  if (rulesError) throw rulesError;

  const enrichedGapRules = await Promise.all(
    (gapRules || []).map(async (rule) => {
      const enriched = { ...rule };

      if (rule.source_group_id) {
        const { data: sourceGroup } = await supabase
          .from("preset_groups")
          .select("*")
          .eq("id", rule.source_group_id)
          .maybeSingle();

        if (sourceGroup) {
          const { data: members } = await supabase
            .from("preset_group_members")
            .select("preset:presets(*)")
            .eq("group_id", sourceGroup.id);

          const presets = (members || [])
            .map((m) => m.preset as Preset)
            .filter(Boolean);

          enriched.source_group = { ...sourceGroup, presets };
        }
      }

      if (rule.target_group_id) {
        const { data: targetGroup } = await supabase
          .from("preset_groups")
          .select("*")
          .eq("id", rule.target_group_id)
          .maybeSingle();

        if (targetGroup) {
          const { data: members } = await supabase
            .from("preset_group_members")
            .select("preset:presets(*)")
            .eq("group_id", targetGroup.id);

          const presets = (members || [])
            .map((m) => m.preset as Preset)
            .filter(Boolean);

          enriched.target_group = { ...targetGroup, presets };
        }
      }

      return enriched;
    })
  );

  return {
    ...schedule,
    entries: enrichedEntries,
    gap_rules: enrichedGapRules,
  };
}

export async function createOrUpdateSchedule(
  entries: Array<{
    preset_id?: string;
    group_id?: string;
    desired_offset_min: number;
    note?: string;
  }>,
  gapRules: Array<{
    source_tag: string | null;
    source_group_id: string | null;
    target_tag: string | null;
    target_group_id: string | null;
    min_gap_min: number;
  }>
): Promise<Schedule> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  // First, deactivate ALL existing schedules to ensure we only have one active
  await supabase
    .from("schedules")
    .update({ is_active: false })
    .eq("user_id", user.user.id);

  // Get the most recent schedule to update, or create new one
  const { data: existingSchedule } = await supabase
    .from("schedules")
    .select("*")
    .eq("user_id", user.user.id)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  let scheduleId: string;

  if (existingSchedule) {
    // Reactivate the existing schedule
    await supabase
      .from("schedules")
      .update({ is_active: true })
      .eq("id", existingSchedule.id);

    scheduleId = existingSchedule.id;

    // Delete existing entries and rules for this schedule
    console.log("Deleting existing entries for schedule:", scheduleId);
    await supabase
      .from("schedule_entries")
      .delete()
      .eq("schedule_id", scheduleId);
    await supabase.from("gap_rules").delete().eq("schedule_id", scheduleId);
  } else {
    // Create new schedule
    const { data: newSchedule, error } = await supabase
      .from("schedules")
      .insert({
        user_id: user.user.id,
        title: "My Daily Schedule",
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;
    scheduleId = newSchedule.id;
  }

  if (entries.length > 0) {
    console.log(
      "Inserting",
      entries.length,
      "new entries for schedule:",
      scheduleId
    );
    const { error: entriesError } = await supabase
      .from("schedule_entries")
      .insert(
        entries.map((e) => ({
          schedule_id: scheduleId,
          preset_id: e.preset_id || null,
          group_id: e.group_id || null,
          desired_offset_min: e.desired_offset_min,
          note: e.note || null,
        }))
      );

    if (entriesError) {
      console.error("Error inserting schedule entries:", entriesError);
      throw entriesError;
    }
  }

  if (gapRules.length > 0) {
    const validGapRules = gapRules.filter(
      (r) =>
        (r.source_tag || r.source_group_id) &&
        (r.target_tag || r.target_group_id)
    );

    if (validGapRules.length > 0) {
      console.log(
        "Inserting",
        validGapRules.length,
        "gap rules for schedule:",
        scheduleId
      );
      const { error: rulesError } = await supabase.from("gap_rules").insert(
        validGapRules.map((r) => ({
          schedule_id: scheduleId,
          source_tag: r.source_tag,
          source_group_id: r.source_group_id,
          target_tag: r.target_tag,
          target_group_id: r.target_group_id,
          min_gap_min: r.min_gap_min,
          relation: "AFTER",
        }))
      );

      if (rulesError) {
        console.error("Error inserting gap rules:", rulesError);
        throw rulesError;
      }
    }
  }

  const { data: schedule } = await supabase
    .from("schedules")
    .select("*")
    .eq("id", scheduleId)
    .single();

  return schedule!;
}

export async function deleteSchedule(): Promise<void> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const { data: existingSchedule } = await supabase
    .from("schedules")
    .select("id")
    .eq("user_id", user.user.id)
    .eq("is_active", true)
    .maybeSingle();

  if (!existingSchedule) {
    throw new Error("No active schedule found to delete");
  }

  try {
    // First, get all schedule entry IDs for this schedule
    const { data: scheduleEntries } = await supabase
      .from("schedule_entries")
      .select("id")
      .eq("schedule_id", existingSchedule.id);

    // Delete any day_plan_items that reference these schedule_entries
    // This is necessary because day_plan_items has ON DELETE RESTRICT for schedule_entries
    if (scheduleEntries && scheduleEntries.length > 0) {
      const entryIds = scheduleEntries.map((entry) => entry.id);

      const { error: dayPlanItemsError } = await supabase
        .from("day_plan_items")
        .delete()
        .in("schedule_entry_id", entryIds);

      if (dayPlanItemsError) {
        console.error("Error deleting day plan items:", dayPlanItemsError);
        throw new Error(
          `Failed to delete day plan items: ${dayPlanItemsError.message}`
        );
      }
    }

    // Delete all day plans for this user since they're now invalid without a schedule
    const { error: dayPlansError } = await supabase
      .from("day_plans")
      .delete()
      .eq("user_id", user.user.id);

    if (dayPlansError) {
      console.error("Error deleting day plans:", dayPlansError);
      throw new Error(`Failed to delete day plans: ${dayPlansError.message}`);
    }

    // Delete schedule entries (this should cascade to gap rules due to foreign key constraints)
    const { error: entriesError } = await supabase
      .from("schedule_entries")
      .delete()
      .eq("schedule_id", existingSchedule.id);

    if (entriesError) {
      console.error("Error deleting schedule entries:", entriesError);
      throw new Error(
        `Failed to delete schedule entries: ${entriesError.message}`
      );
    }

    // Delete gap rules (explicitly, though CASCADE should handle this)
    const { error: rulesError } = await supabase
      .from("gap_rules")
      .delete()
      .eq("schedule_id", existingSchedule.id);

    if (rulesError) {
      console.error("Error deleting gap rules:", rulesError);
      throw new Error(`Failed to delete gap rules: ${rulesError.message}`);
    }

    // Delete the schedule itself
    const { error: scheduleError } = await supabase
      .from("schedules")
      .delete()
      .eq("id", existingSchedule.id);

    if (scheduleError) {
      console.error("Error deleting schedule:", scheduleError);
      throw new Error(`Failed to delete schedule: ${scheduleError.message}`);
    }

    // Log the deletion event
    const { error: eventError } = await supabase.from("events").insert({
      user_id: user.user.id,
      type: "SCHEDULE_DELETED",
      payload: { schedule_id: existingSchedule.id },
    });

    if (eventError) {
      console.error("Error logging deletion event:", eventError);
      // Don't throw here - the deletion was successful even if logging failed
    }
  } catch (error) {
    console.error("Delete schedule failed:", error);
    throw error;
  }
}

export async function wakeUp(): Promise<DayPlan & { items: DayPlanItem[] }> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const { data: existingPlan } = await supabase
    .from("day_plans")
    .select("*")
    .eq("user_id", user.user.id)
    .gte("wake_time", todayStart.toISOString())
    .maybeSingle();

  if (existingPlan) {
    await supabase
      .from("day_plan_items")
      .delete()
      .eq("day_plan_id", existingPlan.id);
    await supabase.from("day_plans").delete().eq("id", existingPlan.id);
  }

  const schedule = await getActiveSchedule();
  if (!schedule || schedule.entries.length === 0) {
    throw new Error("No active schedule found");
  }

  const expandedEntries: Array<{
    id: string;
    preset: Preset;
    desired_offset_min: number;
    groupId?: string;
    groupSnapshot?: { name: string; description?: string };
    groupInstanceId?: string;
  }> = [];

  for (const entry of schedule.entries) {
    if (entry.preset_id && entry.preset) {
      expandedEntries.push({
        id: entry.id,
        preset: entry.preset,
        desired_offset_min: entry.desired_offset_min,
      });
    } else if (entry.group_id && entry.group) {
      const groupInstanceId = crypto.randomUUID();
      const groupSnapshot = {
        name: entry.group.name,
        description: entry.group.description || undefined,
      };

      for (const preset of entry.group.presets) {
        expandedEntries.push({
          id: entry.id,
          preset,
          desired_offset_min: entry.desired_offset_min,
          groupId: entry.group_id,
          groupSnapshot,
          groupInstanceId,
        });
      }
    }
  }

  const plannedItems = generateDayPlan(
    now,
    expandedEntries as any,
    schedule.gap_rules
  );

  const { data: dayPlan, error: planError } = await supabase
    .from("day_plans")
    .insert({
      user_id: user.user.id,
      wake_time: now.toISOString(),
      generated_at: now.toISOString(),
      recompute_count: existingPlan ? existingPlan.recompute_count + 1 : 0,
    })
    .select()
    .single();

  if (planError) throw planError;

  const itemsToInsert = plannedItems.map((item, index) => {
    const expandedEntry = expandedEntries[index];
    return {
      day_plan_id: dayPlan.id,
      schedule_entry_id: item.scheduleEntryId,
      preset_snapshot: item.presetSnapshot,
      desired_time: item.desiredTime.toISOString(),
      planned_time: item.plannedTime.toISOString(),
      is_next_day: isNextDay(now, item.plannedTime),
      shift_minutes: Math.round(
        (item.plannedTime.getTime() - item.desiredTime.getTime()) / 60000
      ),
      status: "PLANNED" as const,
      conflict_notes: item.conflictNotes,
      source_group_id: expandedEntry.groupId || null,
      source_group_snapshot: expandedEntry.groupSnapshot || null,
      group_instance_id: expandedEntry.groupInstanceId || null,
    };
  });

  const { data: items, error: itemsError } = await supabase
    .from("day_plan_items")
    .insert(itemsToInsert)
    .select();

  if (itemsError) throw itemsError;

  await supabase.from("events").insert({
    user_id: user.user.id,
    type: "WAKE",
    payload: { wake_time: now.toISOString(), plan_id: dayPlan.id },
  });

  return { ...dayPlan, items: items || [] };
}

export async function getTodayPlan(): Promise<
  (DayPlan & { items: DayPlanItem[] }) | null
> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const { data: plan } = await supabase
    .from("day_plans")
    .select("*")
    .eq("user_id", user.user.id)
    .gte("wake_time", todayStart.toISOString())
    .order("wake_time", { ascending: false })
    .maybeSingle();

  if (!plan) return null;

  const { data: items } = await supabase
    .from("day_plan_items")
    .select("*")
    .eq("day_plan_id", plan.id)
    .order("planned_time");

  return { ...plan, items: items || [] };
}

export async function markDoseTaken(itemId: string): Promise<DayPlanItem> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const now = new Date();

  const { data: updatedItem } = await supabase
    .from("day_plan_items")
    .update({
      status: "TAKEN",
      actual_time: now.toISOString(),
    })
    .eq("id", itemId)
    .select()
    .single();

  await supabase.from("events").insert({
    user_id: user.user.id,
    type: "DOSE_TAKEN",
    payload: { item_id: itemId, time: now.toISOString() },
  });

  return updatedItem!;
}

export async function markDoseMissed(itemId: string): Promise<DayPlanItem> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const { data: updatedItem } = await supabase
    .from("day_plan_items")
    .update({ status: "MISSED" })
    .eq("id", itemId)
    .select()
    .single();

  await supabase.from("events").insert({
    user_id: user.user.id,
    type: "DOSE_MISSED",
    payload: { item_id: itemId },
  });

  return updatedItem!;
}

export async function snoozeDose(
  itemId: string,
  minutes: number
): Promise<DayPlanItem> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const { data: item } = await supabase
    .from("day_plan_items")
    .select("*, day_plan:day_plans!inner(*)")
    .eq("id", itemId)
    .single();

  if (!item) throw new Error("Item not found");

  const newPlannedTime = new Date(
    new Date(item.planned_time).getTime() + minutes * 60000
  );

  const { data: updatedItem } = await supabase
    .from("day_plan_items")
    .update({
      planned_time: newPlannedTime.toISOString(),
      status: "SNOOZED",
    })
    .eq("id", itemId)
    .select()
    .single();

  await supabase
    .from("scheduled_notifications")
    .update({
      scheduled_for: newPlannedTime.toISOString(),
      sent: false,
      sent_at: null,
    })
    .eq("item_id", itemId)
    .eq("user_id", user.user.id);

  await supabase.from("events").insert({
    user_id: user.user.id,
    type: "SNOOZE",
    payload: {
      item_id: itemId,
      minutes,
      new_time: newPlannedTime.toISOString(),
    },
  });

  return updatedItem!;
}

export async function clearTodayPlan(): Promise<void> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const { data: plan } = await supabase
    .from("day_plans")
    .select("id")
    .eq("user_id", user.user.id)
    .gte("wake_time", todayStart.toISOString())
    .order("wake_time", { ascending: false })
    .maybeSingle();

  if (!plan) return;

  await supabase.from("day_plan_items").delete().eq("day_plan_id", plan.id);
  await supabase.from("day_plans").delete().eq("id", plan.id);

  await supabase.from("events").insert({
    user_id: user.user.id,
    type: "PLAN_CLEARED",
    payload: { plan_id: plan.id },
  });
}

export async function getUserGroups(): Promise<
  (PresetGroup & { presets: Preset[] })[]
> {
  const { data: groups, error: groupsError } = await supabase
    .from("preset_groups")
    .select("*")
    .order("name");

  if (groupsError) throw groupsError;
  if (!groups || groups.length === 0) return [];

  const { data: members, error: membersError } = await supabase
    .from("preset_group_members")
    .select("*, preset:presets(*)");

  if (membersError) throw membersError;

  const groupsWithPresets = groups.map((group) => ({
    ...group,
    presets: (members || [])
      .filter((m) => m.group_id === group.id)
      .map((m) => m.preset as Preset)
      .filter(Boolean),
  }));

  return groupsWithPresets;
}

export async function createGroup(
  name: string,
  presetIds: string[],
  description?: string
): Promise<PresetGroup> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const { data: group, error: groupError } = await supabase
    .from("preset_groups")
    .insert({
      user_id: user.user.id,
      name,
      description: description || null,
    })
    .select()
    .single();

  if (groupError) throw groupError;

  if (presetIds.length > 0) {
    const { error: membersError } = await supabase
      .from("preset_group_members")
      .insert(
        presetIds.map((preset_id) => ({ group_id: group.id, preset_id }))
      );

    if (membersError) throw membersError;
  }

  return group;
}

export async function updateGroup(
  groupId: string,
  name: string,
  presetIds: string[],
  description?: string
): Promise<PresetGroup> {
  const { data: group, error: groupError } = await supabase
    .from("preset_groups")
    .update({ name, description: description || null })
    .eq("id", groupId)
    .select()
    .single();

  if (groupError) throw groupError;

  await supabase.from("preset_group_members").delete().eq("group_id", groupId);

  if (presetIds.length > 0) {
    const { error: membersError } = await supabase
      .from("preset_group_members")
      .insert(presetIds.map((preset_id) => ({ group_id: groupId, preset_id })));

    if (membersError) throw membersError;
  }

  return group;
}

export async function deleteGroup(groupId: string): Promise<void> {
  const { error } = await supabase
    .from("preset_groups")
    .delete()
    .eq("id", groupId);

  if (error) throw error;
}

export async function getGroupsForPreset(
  presetId: string
): Promise<PresetGroup[]> {
  const { data: members, error: membersError } = await supabase
    .from("preset_group_members")
    .select("group:preset_groups(*)")
    .eq("preset_id", presetId);

  if (membersError) throw membersError;

  return (members || []).map((m) => m.group as PresetGroup).filter(Boolean);
}
